import { useState } from 'react';
import {
  Icon,
  Spinner,
  ToastId,
  useToast,
  UseToastOptions,
} from '@chakra-ui/react';
import { useOktaAuth } from '@okta/okta-react';
import { captureException } from '@sentry/react';
import { BiErrorCircle } from 'react-icons/bi';

import { fetchWithAuth } from '../../auth';
import {
  FileValidationConfirmationModalContent,
  FileValidationFailingModalContent,
  ValidationError,
} from '../../components/file-upload-dialog/file-upload-dialog';
import { useImperativeModal } from '../../context/imperative-modal/imperative-modal';
import {
  useValidateFilenamesMutation,
} from '../../graphql/generated';
import { FileUploadService } from '../../services/file-upload.service';
import {
  EnhancedFile,
  formatFilePath,
} from '../../utils/file/file';
import { transformFiles } from './file-validation/file-validation';
import { getValidateFilesMutationInput } from './file-validation/validation-utils';
import {
  FailedUploads,
  Notification,
  notifyUploadResult,
} from './notifications';

type UploadCompleteHandler = {
  onUploadComplete: () => void;
};

type DirectoryFile = {
  status?: string | null;
  name: string;
  updatedAt?: string | null;
  createdAt?: string | null;
};

type UploadFilesParams = {
  files: EnhancedFile[];
  cedantId: string;
  pathId?: string;
  directoryFiles?: DirectoryFile[];
};

type UseFileUploadBag = {
  isOpen: boolean;
  upload: (params: UploadFilesParams & UploadCompleteHandler) => void;
  isUploading: boolean;
};

const isFileExtensionError = (error: Error) => {
  const pattern = new RegExp('extension(.*)is not supported/allowed');
  return pattern.test(error.message);
};

const trackFileUpload = (
  file: EnhancedFile,
  pathId: string | undefined,
  status: string,
  setRecentUploads: React.Dispatch<React.SetStateAction<Map<string, {timestamp: number, status: string}>>>
) => {
  const dirPath = pathId ? formatFilePath(pathId) : '';
  const fullPath = dirPath ? `${dirPath}/${file.file.name}` : file.file.name;
  setRecentUploads(prev => new Map(prev.set(fullPath, {
    timestamp: Date.now(),
    status
  })));
};

const showDuplicateError = (duplicateNames: string, toast: ReturnType<typeof useToast>) => {
  toast({
    status: 'error',
    title: 'Upload blocked',
    description: `${duplicateNames} is currently being processed. Please wait 90 seconds before uploading again.`,
    duration: 3000,
    isClosable: true,
  });
};

export const useFileUpload = (): UseFileUploadBag => {
  const toast = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [recentUploads, setRecentUploads] = useState<Map<string, {timestamp: number, status: string}>>(new Map());
  const { openModal, closeModal, isOpen } = useImperativeModal();
  const [validateFilesMutation] = useValidateFilenamesMutation();
  const { oktaAuth } = useOktaAuth();

  const showToast = (options: UseToastOptions, id?: ToastId) => {
    if (!id) return;
    toast.update(id, options);
  };

  const finishFileUpload = (
    failedUploads: FailedUploads,
    files: EnhancedFile[],
    onUploadComplete: () => void,
    toastId: ToastId
  ) => {
    notifyUploadResult(failedUploads, files, showToast, toastId);
    setIsUploading(false);
    onUploadComplete();
  };

  const handleCancelUpload = () => {
    setIsUploading(false);
    closeModal();
  };

  const checkRecentUploads = (files: EnhancedFile[], pathId: string | undefined, now: number) => {
    const blockedFiles: string[] = [];
    
    files.forEach(file => {
      const dirPath = pathId ? formatFilePath(pathId) : '';
      const fullPath = dirPath ? `${dirPath}/${file.file.name}` : file.file.name;
      const lastUpload = recentUploads.get(fullPath);
      
      if (lastUpload && 
          ['AWAITING_UPLOAD', 'PROCESSING', 'VALIDATING'].includes(lastUpload.status) &&
          (now - lastUpload.timestamp) < 90000) {
        blockedFiles.push(file.file.name);
      }
    });
    
    return blockedFiles;
  };

  const checkDirectoryFiles = (directoryFiles: DirectoryFile[], now: number) => {
    return directoryFiles
      .filter(file => {
        if (!file.status || !['AWAITING_UPLOAD', 'PROCESSING'].includes(file.status)) return false;
        
        const fileTime = new Date(file.updatedAt || file.createdAt || 0);
        const timeDiffSeconds = (now - fileTime.getTime()) / 1000;
        
        return timeDiffSeconds < 90;
      })
      .map(file => file.name);
  };

  const checkDuplicateFiles = (files: EnhancedFile[], directoryFiles: DirectoryFile[] = [], pathId?: string) => {
    const now = Date.now();
    
    const recentlyBlockedFiles = checkRecentUploads(files, pathId, now);
    const processingFileNames = checkDirectoryFiles(directoryFiles, now);
    
    const allBlockedFiles = [...new Set([...recentlyBlockedFiles, ...processingFileNames])];
    
    const validFiles = files.filter(file => !allBlockedFiles.includes(file.file.name));
    const duplicateFiles = files.filter(file => allBlockedFiles.includes(file.file.name));

    return { validFiles, duplicateFiles };
  };

  const handleConfirmUpload = (
    params: UploadFilesParams & UploadCompleteHandler
  ) => () => {
    const { validFiles, duplicateFiles } = checkDuplicateFiles(params.files, params.directoryFiles, params.pathId);
    
    if (duplicateFiles.length > 0) {
      const duplicateNames = duplicateFiles.map(f => f.file.name).join(', ');
      showDuplicateError(duplicateNames, toast);
      closeModal();
      return;
    }
    
    // Track renamed files
    validFiles.forEach(file => {
      trackFileUpload(file, params.pathId, 'AWAITING_UPLOAD', setRecentUploads);
    });
    
    closeModal();
    uploadFiles({ ...params, files: validFiles });
  };

  const handleUploadError = (error: Error, attempt: number, maxRetries: number, enhancedFile: EnhancedFile) => {
    if (error.message === 'TOKEN_EXPIRED') {
      return { action: 'return', value: 'TOKEN_EXPIRED' };
    }

    if (isFileExtensionError(error)) {
      return { action: 'return', value: error.message };
    }

    if (error.message.includes('500') && attempt < maxRetries) {
      return { action: 'retry', value: null };
    }

    if (attempt === maxRetries) {
      captureException(error);
      return { action: 'return', value: `Error uploading ${enhancedFile.path}: ${error.message}` };
    }

    return { action: 'continue', value: null };
  };

  const uploadFileWithRetry = async (
    enhancedFile: EnhancedFile,
    cedantId: string,
    pathId: string | undefined,
    token: string,
    maxRetries = 3
  ): Promise<string | null> => {
    const newFile = new File([enhancedFile.file], enhancedFile.file.name, {
      type: enhancedFile.file.type,
    });

    const { path: filepath } = enhancedFile;
    const dirPath = pathId ? formatFilePath(pathId) : undefined;
    const targetPath = dirPath ? `${dirPath}/${filepath}` : filepath;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await FileUploadService.uploadFile({
          cedant: cedantId,
          fileName: targetPath,
          contentType: newFile.type,
          file: newFile,
        }, token, fetchWithAuth);
        
        return null;
        
      } catch (e: unknown) {
        const error = e as Error;
        const errorResult = handleUploadError(error, attempt, maxRetries, enhancedFile);
        
        if (errorResult.action === 'return') {
          return errorResult.value;
        }
        
        if (errorResult.action === 'retry') {
          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          continue;
        }
      }
    }

    return `Error uploading ${enhancedFile.path}: Max retries exceeded`;
  };

  const processBatchWithTokenRefresh = async (
    batch: EnhancedFile[],
    cedantId: string,
    pathId: string | undefined,
    currentToken: string
  ): Promise<{ results: (string | null)[]; updatedToken: string }> => {
    const batchResults: (string | null)[] = [];
    let batchToken = currentToken;
    
    for (const enhancedFile of batch) {
      let result = await uploadFileWithRetry(enhancedFile, cedantId, pathId, batchToken);
      
      if (result === 'TOKEN_EXPIRED') {
        const newToken = await oktaAuth.getAccessToken();
        if (newToken) {
          batchToken = newToken;
          result = await uploadFileWithRetry(enhancedFile, cedantId, pathId, batchToken);
        } else {
          result = 'Authentication failed. Please refresh and try again.';
        }
      }
      
      batchResults.push(result);
    }
    
    return { results: batchResults, updatedToken: batchToken };
  };

  const showBatchProgress = (files: EnhancedFile[], currentIndex: number, batchSize: number, toastId: ToastId) => {
    const progress = Math.round(((currentIndex / files.length) * 100));
    const uploadingNotification = Notification.uploading({
      size: 'full-width',
      icon: <Spinner color="white" size="xs" />,
      title: `Uploading ${files.length} files (${progress}% complete)`,
      subtitle: `Processing batch ${Math.floor(currentIndex / batchSize) + 1} of ${Math.ceil(files.length / batchSize)}`,
    });
    showToast(uploadingNotification, toastId);
  };

  const uploadFiles = async ({
    files,
    pathId,
    cedantId,
    onUploadComplete,
  }: UploadFilesParams & UploadCompleteHandler) => {
    const currentToastId = toast();
    const failedUploads: FailedUploads = [];
    const BATCH_SIZE = 10;
    
    const initialToken = await oktaAuth.getAccessToken();
    if (!initialToken) {
      throw new Error('No access token available');
    }
    let currentToken: string = initialToken;

    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      
      if (currentToastId) {
        showBatchProgress(files, i, BATCH_SIZE, currentToastId);
      }
      
      const { results, updatedToken } = await processBatchWithTokenRefresh(
        batch, cedantId, pathId, currentToken
      );
      currentToken = updatedToken;
      
      results.forEach((result, index) => {
        if (result) {
          failedUploads.push([batch[index].path, result]);
        }
      });

      if (i + BATCH_SIZE < files.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    if (currentToastId) {
      finishFileUpload(failedUploads, files, onUploadComplete, currentToastId);
    } else {
      setIsUploading(false);
      onUploadComplete();
    }
    return { failedUploads };
  };

  const executeValidation = async (cedantId: string, files: EnhancedFile[]) => {
    const {
      data: fileValidationData,
      errors: fileValidationErrors,
    } = await validateFilesMutation({
      variables: getValidateFilesMutationInput(cedantId, files),
    });

    if (!fileValidationData || fileValidationErrors) {
      throw new Error('Error validating files');
    }

    return fileValidationData.validateFilenames;
  };

  const validateFiles = async ({ cedantId, files }: UploadFilesParams) => {
    try {
      return await executeValidation(cedantId, files);
    } catch (error: unknown) {
      const err = error as { networkError?: { statusCode?: number } };
      if (err.networkError?.statusCode === 401) {
        await oktaAuth.getAccessToken();
        return await executeValidation(cedantId, files);
      }
      throw error;
    }
  };

  const handleDuplicateFiles = (duplicateFiles: EnhancedFile[]) => {
    if (duplicateFiles.length === 0) return false;
    
    const duplicateNames = duplicateFiles.map(f => f.file.name).join(', ');
    showDuplicateError(duplicateNames, toast);
    return true;
  };

  const createValidationModal = (
    validationErrors: ValidationError,
    validatedFiles: EnhancedFile[],
    params: UploadFilesParams & UploadCompleteHandler
  ) => {
    return validatedFiles.length > 0 ? (
      <FileValidationConfirmationModalContent
        errors={validationErrors}
        onCancel={handleCancelUpload}
        onConfirm={handleConfirmUpload(params)}
      />
    ) : (
      <FileValidationFailingModalContent
        errors={validationErrors}
        onCancel={handleCancelUpload}
      />
    );
  };

  const processValidatedFiles = async (
    validFiles: EnhancedFile[],
    params: UploadFilesParams & UploadCompleteHandler
  ) => {
    const { cedantId, pathId, onUploadComplete } = params;
    
    // Track files during validation
    validFiles.forEach(file => {
      trackFileUpload(file, pathId, 'VALIDATING', setRecentUploads);
    });

    const validatedFilenames = await validateFiles({ cedantId, files: validFiles });
    const { errors, validatedFiles, hasValidationErrors } = await transformFiles({ 
      files: validFiles, 
      validatedFilenames 
    });

    if (hasValidationErrors) {
      const modalContent = createValidationModal(
        errors as ValidationError,
        validatedFiles,
        { ...params, files: validatedFiles }
      );
      return openModal(modalContent);
    }

    // Track upload times
    validatedFiles.forEach(file => {
      trackFileUpload(file, pathId, 'AWAITING_UPLOAD', setRecentUploads);
    });
    
    uploadFiles({ pathId, cedantId, onUploadComplete, files: validatedFiles });
  };

  const handleUploadProcess = async (params: UploadFilesParams & UploadCompleteHandler) => {
    const { files, directoryFiles = [], pathId } = params;
    
    setIsUploading(true);
    try {
      const { validFiles, duplicateFiles } = checkDuplicateFiles(files, directoryFiles, pathId);
      
      if (handleDuplicateFiles(duplicateFiles)) {
        setIsUploading(false);
        return;
      }

      await processValidatedFiles(validFiles, params);
    } catch (error) {
      const errorToastId = toast();
      const uploadFailed = Notification.uploading({
        size: 'full-width',
        variant: 'error',
        title: `Failed to upload files`,
        icon: <Icon as={BiErrorCircle} fontSize="1.4rem" />,
      });
      showToast(uploadFailed, errorToastId);
    } finally {
      setIsUploading(false);
    }
  };

  const upload = (params: UploadFilesParams & UploadCompleteHandler) => {
    handleUploadProcess(params);
  };

  return {
    isOpen,
    upload,
    isUploading: isUploading,
  };
};